import { FormikErrors, setIn } from "formik";
import { z } from "zod";
import { v4 as uuid } from "uuid";
import { addCalendarDays, toUTCDateOnly } from "@common/helpers/date";
import { isLockedByDateOnly } from "@helpers/lock";
import type {
    JournalEntryDrawerFormikConfig,
    JournalEntryLineProxyObject,
} from "./JournalEntryDrawer";
import {
    buildEmptyLines,
    DEFAULT_NUMBER_OF_LINES,
} from "./JournalEntryDrawerUtils";

// Fixing Formik's bad design: https://github.com/jaredpalmer/formik/issues/1251
export const JournalEntryDrawerMutableConfig = {
    validate: false,
};

interface JournalEntryLineValidationShape {
    accountId?: string | null;
    debitAmount: JournalEntryAmountValue;
    creditAmount: JournalEntryAmountValue;
}

type JournalEntryAmountValue = number | string | null;

type AmountField = "debitAmount" | "creditAmount";
const AMOUNT_FIELDS: AmountField[] = ["debitAmount", "creditAmount"];

interface JournalEntryValidationValues {
    journalEntry: {
        lines: JournalEntryLineValidationShape[];
    };
}

type JournalEntryValidationErrors =
    FormikErrors<JournalEntryValidationValues> & {
        debitAndCreditSums?: string;
    };

interface ValidateJournalEntryFormArgs {
    values: JournalEntryValidationValues;
    accountIdSet: Set<string>;
    submittedWithErrors: boolean;
    forceValidate: boolean;
}

interface ValidateJournalEntryFormResult {
    errors: JournalEntryValidationErrors;
    shouldMarkSubmittedWithErrors: boolean;
    didConsumeForceValidate: boolean;
}

const addPositiveAmountIssue = ({
    value,
    field,
    ctx,
}: {
    value: JournalEntryAmountValue;
    field: AmountField;
    ctx: z.RefinementCtx;
}) => {
    if (value === null || value === "") {
        return;
    }

    const amount = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(amount) || amount <= 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be greater than 0",
            path: [field],
        });
    }
};

const lineValidationSchema = z
    .object({
        accountId: z.string().nullable().optional(),
        debitAmount: z.union([z.number(), z.string(), z.null()]),
        creditAmount: z.union([z.number(), z.string(), z.null()]),
    })
    .superRefine((line, ctx) => {
        const hasDebitAmount = Boolean(line.debitAmount);
        const hasCreditAmount = Boolean(line.creditAmount);
        const shouldHaveAccountId = hasDebitAmount || hasCreditAmount;
        const accountId = line.accountId ?? "";

        if (!shouldHaveAccountId && accountId) {
            for (const field of AMOUNT_FIELDS) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Enter a debit or credit",
                    path: [field],
                });
            }
            return;
        }

        if (!shouldHaveAccountId) {
            addPositiveAmountIssue({
                value: line.debitAmount,
                field: "debitAmount",
                ctx,
            });
            addPositiveAmountIssue({
                value: line.creditAmount,
                field: "creditAmount",
                ctx,
            });
            return;
        }

        if (!accountId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Required",
                path: ["accountId"],
            });
        }

        addPositiveAmountIssue({
            value: line.debitAmount,
            field: "debitAmount",
            ctx,
        });
        addPositiveAmountIssue({
            value: line.creditAmount,
            field: "creditAmount",
            ctx,
        });
    });

const getDebitAndCreditSumsError = (
    lines: JournalEntryLineValidationShape[],
): string | undefined => {
    const debitAndCreditSums = lines.reduce(
        (acc, line) => {
            if (line.debitAmount) {
                acc.debit += Number(line.debitAmount);
            }
            if (line.creditAmount) {
                acc.credit += Number(line.creditAmount);
            }
            return acc;
        },
        {
            debit: 0,
            credit: 0,
        },
    );

    const hasAmounts = debitAndCreditSums.debit || debitAndCreditSums.credit;
    const areBalanced =
        Number(debitAndCreditSums.debit).toFixed(2) ===
        debitAndCreditSums.credit.toFixed(2);

    if (hasAmounts && !areBalanced) {
        return "Please check that your total debits match total credits";
    }
};

// Built once at module level — not recreated on every validation call.
const lineArraySchema = z.array(lineValidationSchema);

const validateLines = ({
    lines,
    accountIdSet,
}: {
    lines: JournalEntryLineValidationShape[];
    accountIdSet: Set<string>;
}): JournalEntryValidationErrors["journalEntry"] => {
    const result = lineArraySchema.safeParse(lines);

    let journalEntryErrors: JournalEntryValidationErrors["journalEntry"];

    if (!result.success) {
        for (const issue of result.error.issues) {
            if (
                issue.path.length < 2 ||
                typeof issue.path[0] !== "number" ||
                typeof issue.path[1] !== "string"
            ) {
                continue;
            }
            journalEntryErrors = setIn(
                journalEntryErrors ?? {},
                `lines.${issue.path[0]}.${issue.path[1]}`,
                issue.message,
            );
        }
    }

    // Account-ID membership check — O(1) per line with a Set.
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const hasDebitAmount = Boolean(line.debitAmount);
        const hasCreditAmount = Boolean(line.creditAmount);
        const shouldHaveAccountId = hasDebitAmount || hasCreditAmount;
        const accountId = line.accountId ?? "";

        if (shouldHaveAccountId && accountId && !accountIdSet.has(accountId)) {
            journalEntryErrors = setIn(
                journalEntryErrors ?? {},
                `lines.${i}.accountId`,
                "Invalid account code",
            );
        }
    }

    return journalEntryErrors;
};

export const validateJournalEntryForm = ({
    values,
    accountIdSet,
    submittedWithErrors,
    forceValidate,
}: ValidateJournalEntryFormArgs): ValidateJournalEntryFormResult => {
    if (!forceValidate && !submittedWithErrors) {
        return {
            errors: { journalEntry: undefined },
            shouldMarkSubmittedWithErrors: false,
            didConsumeForceValidate: false,
        };
    }

    const errors: JournalEntryValidationErrors = {};

    const debitAndCreditSumsError = getDebitAndCreditSumsError(
        values.journalEntry.lines,
    );
    if (debitAndCreditSumsError) {
        errors.debitAndCreditSums = debitAndCreditSumsError;
    }

    const journalEntryErrors = validateLines({
        lines: values.journalEntry.lines,
        accountIdSet,
    });
    if (journalEntryErrors) {
        errors.journalEntry = journalEntryErrors;
    }

    return {
        errors,
        shouldMarkSubmittedWithErrors: forceValidate,
        didConsumeForceValidate: forceValidate,
    };
};

export interface JournalEntryDuplicateDraft {
    date: string;
    memo: string;
    lines: JournalEntryLineProxyObject[];
    ledgerId?: string;
}

export interface JournalEntryDuplicateRequest {
    values: JournalEntryDrawerFormikConfig;
    lockDate: Date | null;
}

export const isFilledLine = (line: JournalEntryLineProxyObject) =>
    !!line.accountId || !!line.debitAmount || !!line.creditAmount;

interface BuildJournalEntryDuplicateArgs extends Pick<
    JournalEntryDrawerFormikConfig,
    "journalEntry" | "ledgerId"
> {
    lockDate: Date | null;
    today: string;
}

const getJournalEntryDuplicateDate = ({
    journalEntry,
    lockDate,
    today,
}: Pick<
    BuildJournalEntryDuplicateArgs,
    "journalEntry" | "lockDate" | "today"
>) => {
    if (!isLockedByDateOnly(lockDate, toUTCDateOnly(journalEntry.date))) {
        return journalEntry.date;
    }
    if (!lockDate || !isLockedByDateOnly(lockDate, today)) {
        return today;
    }
    return addCalendarDays(toUTCDateOnly(lockDate), 1);
};

export const buildJournalEntryDuplicate = ({
    journalEntry,
    ledgerId,
    lockDate,
    today,
}: BuildJournalEntryDuplicateArgs): JournalEntryDuplicateDraft => {
    const lines = journalEntry.lines.filter(isFilledLine).map((line) => ({
        id: uuid(),
        accountId: line.accountId,
        debitAmount: line.debitAmount,
        creditAmount: line.creditAmount,
        description: line.description,
        classIds: line.classIds,
        counterpartyId: line.counterpartyId,
        excludeFrom1099: line.excludeFrom1099,
    }));

    return {
        date: getJournalEntryDuplicateDate({ journalEntry, lockDate, today }),
        memo: journalEntry.memo,
        lines: [
            ...lines,
            ...buildEmptyLines(
                Math.max(DEFAULT_NUMBER_OF_LINES - lines.length, 0),
            ),
        ],
        ledgerId,
    };
};
